import React, { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Check, Power, Settings, Zap, Gauge, Activity, Ruler, TrendingUp, TrendingDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ResponsiveImage from '../components/ResponsiveImage';
import ImageCarousel from '../components/ImageCarousel';
import BannersCarousel from '../components/BannersCarousel';
import CatalogueModal from '../components/CatalogueModal';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const bannerImages = [
    "/assets/images/bj30-hybrid/banners/exterior.jpg",
    "/assets/images/bj30-hybrid/banners/Interior.jpg",
    "/assets/images/bj30-hybrid/banners/Rear.jpg"
];

const BJ30 = () => {
    const { language } = useLanguage();
    const t = translations[language].models.bj30;
    const [isCatalogueOpen, setIsCatalogueOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const renderWithBreaks = (text) =>
        text.split("\n").map((line, i) => (
            <span key={i}>
                {line}
                <br />
            </span>
        ));

    return (
        <div className="bg-white text-gray-900 font-sans selection:bg-red-100 mt-20 lg:mt-[120px]">
            <SEO
                title="BAIC BJ30 Hybrid SUV Oman | Efficient & Stylish"
                description="Discover the BAIC BJ30 Hybrid SUV. A perfect blend of efficiency, style, and technology for the Oman roads."
                canonicalUrl="https://baicoman.com/off-road/bj30-hybrid"
            />
            <h1 className="sr-only">BAIC BJ30 Hybrid SUV</h1>

            {/* 1. Hero Section - Main Banner */}
            <BannersCarousel
                banners={bannerImages.map(img => ({
                    image: img,
                    title: "BJ30 HYBRID",
                    subtitle: (
                        <>
                            <span className="font-bold">{t.hero.title}</span>
                            <br />
                            <span className="text-white text-xs md:text-xl lg:text-2xl mb-4 md:mb-8 max-w-3xl font-light leading-relaxed abs animate-fade-in-600">
                                {/* {t.hero.subtitle} */} {renderWithBreaks(t.hero.subtitle)}
                            </span>
                        </>
                    )

                }))}
                use1080={true}
            />

            {/* 2. Introduction / Carousel images stacked */}
            <section className="relative bg-black text-white py-24">
                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="space-y-8"
                    >
                        <h2 className="text-4xl md:text-6xl font-bold leading-tight">
                            {t.intro.title.split(" ").slice(0, -1).join(" ")} <span className="text-red-600">{t.intro.title.split(" ").slice(-1)}</span>
                        </h2>
                        <p className="text-gray-400 text-lg leading-relaxed font-light">
                            {t.intro.desc}
                        </p>
                        <div className="flex gap-8 flex-wrap">
                            <div className="flex flex-col">
                                <span className="text-xl font-bold text-red-600">{t.intro.stats.turbo.value}</span>
                                <span className="text-xs text-gray-500 uppercase tracking-widest">{t.intro.stats.turbo.label}</span>
                            </div>
                            <div className="w-px bg-gray-700 h-12 hidden md:block" />
                            <div className="flex flex-col">
                                <span className="text-xl font-bold text-red-600">{t.intro.stats.hp.value}</span>
                                <span className="text-xs text-gray-500 uppercase tracking-widest">{t.intro.stats.hp.label}</span>
                            </div>
                            <div className="w-px bg-gray-700 h-12 hidden md:block" />
                            <div className="flex flex-col">
                                <span className="text-xl font-bold text-red-600">{t.intro.stats.torque.value}</span>
                                <span className="text-xs text-gray-500 uppercase tracking-widest">{t.intro.stats.torque.label}</span>
                            </div>
                            <div className="w-px bg-gray-700 h-12 hidden md:block" />
                            <div className="flex flex-col">
                                <span className="text-xl font-bold text-red-600">{t.intro.stats.accel.value}</span>
                                <span className="text-xs text-gray-500 uppercase tracking-widest">{t.intro.stats.accel.label}</span>
                            </div>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="relative h-[500px] overflow-hidden rounded-2xl"
                    >
                        <img src="/assets/images/bj30-hybrid/bj30-image2.png" alt="BJ30 Side Profile" className="w-full h-full object-contain" />
                    </motion.div>
                </div>
            </section>

            {/* 3. Exterior Gallery (Mosaic) */}
            <section className="py-0">
                <div className="grid grid-cols-1 md:grid-cols-2">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="relative group overflow-hidden"
                    >
                        <img src="/assets/images/bj30-hybrid/family-face1.jpg" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Front Design" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-12">
                            <div>
                                <h3 className="text-3xl font-bold text-white mb-2">{t.exterior.face.title}</h3>
                                <p className="text-gray-300">{t.exterior.face.desc}</p>
                            </div>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="relative group overflow-hidden"
                    >
                        <img src="/assets/images/bj30-hybrid/Intelligent-led.jpg" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Headlights" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-12">
                            <div>
                                <h3 className="text-3xl font-bold text-white mb-2">{t.exterior.led.title}</h3>
                                <p className="text-gray-300">{t.exterior.led.desc}</p>
                            </div>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        viewport={{ once: true }}
                        className="relative group overflow-hidden md:col-span-2"
                    >
                        <img src="/assets/images/bj30-hybrid/rugge-stance.jpg" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Rear Design" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-12">
                            <div>
                                <h3 className="text-3xl font-bold text-white mb-2">{t.exterior.rear.title}</h3>
                                <p className="text-gray-300">{t.exterior.rear.desc}</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 4. Interior Showcase */}
            <section className="bg-gray-900 py-24 text-white">
                <div className="max-w-7xl mx-auto px-4 mb-16 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <span className="text-red-600 font-bold uppercase tracking-widest text-sm">{t.interior.tag}</span>
                        <h2 className="text-5xl font-bold mt-4">{t.interior.title}</h2>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="relative h-[80vh] w-full"
                >
                    <img src="/assets/images/bj30-hybrid/integrated-instrument-panel.jpg" className="w-full h-full object-cover" alt="Interior Dashboard" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent h-48 flex items-end justify-center pb-12">
                        <p className="text-xl font-light opacity-80">{t.interior.dashboard}</p>
                    </div>
                </motion.div>

                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 -mt-2 relative z-10">
                    {[
                        { img: "/assets/images/bj30-hybrid/electronic-gear.jpg", title: t.interior.features.gear.title, desc: t.interior.features.gear.desc },
                        { img: "/assets/images/bj30-hybrid/ambient-light.jpg", title: t.interior.features.light.title, desc: t.interior.features.light.desc },
                        { img: "/assets/images/bj30-hybrid/flexible-combination.jpg", title: t.interior.features.space.title, desc: t.interior.features.space.desc }
                    ].map((item, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.2 }}
                            viewport={{ once: true }}
                            className="bg-gray-800 p-8 rounded-2xl border border-gray-700 hover:border-red-600 transition-colors"
                        >
                            <div className="h-48 mb-6 overflow-hidden rounded-xl">
                                <img src={item.img} className="w-full h-full object-cover" alt={item.title} />
                            </div>
                            <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                            <p className="text-gray-400 text-sm">{item.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* 5. Performance / Hybrid */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="order-2 lg:order-1"
                    >
                        <img src="/assets/images/bj30-hybrid/hybrid.jpg" className="w-full rounded-2xl shadow-2xl" alt="Hybrid System" />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="order-1 lg:order-2 space-y-8"
                    >
                        <h5 className="text-red-600 font-bold uppercase tracking-widest">{t.performance.tag}</h5>
                        <h2 className="text-5xl font-bold text-gray-900 leading-tight">{t.performance.title}</h2>
                        <p className="text-gray-500 text-lg leading-relaxed">
                            {t.performance.desc}
                        </p>
                        <ul className="space-y-4">
                            {t.performance.features.map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-gray-800 font-medium">
                                    <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                                        <Check className="w-3 h-3" />
                                    </div>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </section>

            {/* 6. ATS Text Information */}
            <section className="bg-white">
                <div className="max-w-5xl mx-auto px-6 py-16 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-6">
                            {t.ats.title}
                        </h2>
                        <div className="h-px bg-gray-300 w-full mb-8"></div>
                        <p className="text-base text-gray-600 leading-relaxed max-w-4xl mx-auto">
                            • {t.ats.desc}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 7. ATS / Offroad - Hotspots */}
            <section className="bg-black w-full text-white py-12 lg:py-24">
                <div className="max-w-[1400px] mx-auto relative px-4">
                    {/* Title Section */}
                    <div className="text-center mb-12 lg:mb-20">
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className={`text-3xl md:text-5xl font-bold uppercase tracking-wider mb-4 ${language === 'ar' ? 'font-arabic' : ''}`}
                        >
                            {language === 'ar' ? 'تصميم ذكي لكل التضاريس' : 'Intelligent All-Terrain Design'}
                        </motion.h2>
                        <div className="h-1 w-24 bg-red-600 mx-auto rounded-full" />
                    </div>

                    {/* Interactive Container - Desktop (Horizontal Image) */}
                    <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl bg-zinc-900 ring-1 ring-white/10 ">
                        <img
                            src="/assets/images/bj30-hybrid/ats-main-horizontal.jpg"
                            alt="BJ30 Offroad Capabilities"
                            className="w-full h-auto block"
                        />
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/20 via-transparent to-transparent" />

                        {t.atsPointsDesktop && t.atsPointsDesktop.map((p) => (
                            <div
                                key={p.id}
                                className="absolute group z-10"
                                style={{
                                    left: `${p.x}%`,
                                    top: `${p.y}%`,
                                    transform: 'translate(-50%, -50%)',
                                }}
                            >
                                <div className="absolute -inset-4 bg-red-600/20 rounded-full blur-md animate-pulse group-hover:bg-red-600/40 transition-colors" />
                                {p.id === 'approach' ? (
                                    <img
                                        src="/assets/images/bj30-hybrid/approach-angle.png"
                                        alt="Approach Angle"
                                        className="relative w-12 h-12 md:w-28 md:h-28 object-contain z-20 transition-transform duration-300 group-hover:scale-110"
                                    />
                                ) : p.id === 'departure' ? (
                                    <img
                                        src="/assets/images/bj30-hybrid/departure-angle.png"
                                        alt="Departure Angle"
                                        className="relative w-12 h-12 md:w-28 md:h-28 object-contain z-20 transition-transform duration-300 group-hover:scale-110"
                                    />
                                ) : (
                                    <div className="relative w-3 h-3 md:w-5 md:h-5 bg-white border-2 border-red-600 rounded-full shadow-[0_0_20px_rgba(220,38,38,0.6)] z-20 transition-transform duration-300 group-hover:scale-125" />
                                )}

                                {p.id === 'mark' ? (
                                    <div className="absolute left-full top-1/2 -translate-y-1/2 bg-gradient-to-r from-red-600/0 via-red-600/80 to-red-600/0 h-px w-8 md:w-24 transition-all duration-500 ease-out origin-left" />
                                ) : (p.id !== 'approach' && p.id !== 'departure') && (
                                    <div className={`absolute left-1/2 -translate-x-1/2 bg-gradient-to-b from-red-600/0 via-red-600/80 to-red-600/0 w-px transition-all duration-500 ease-out origin-top ${p.id === 'hardTop' ? 'h-4 md:h-12' : 'h-8 md:h-24'} ${p.y <= 50 ? 'bottom-full bg-gradient-to-t' : 'top-full'}`} />
                                )}

                                <div
                                    className={`absolute transform w-max max-w-[150px] md:max-w-[250px] text-center transition-all duration-500 ${p.id === 'mark'
                                        ? 'left-[calc(100%+32px)] md:left-[calc(100%+96px)] top-1/2 -translate-y-1/2 text-left'
                                        : (p.id === 'approach' || p.id === 'departure')
                                            ? 'top-[calc(100%+5px)] left-1/2 -translate-x-1/2'
                                            : p.y <= 50
                                                ? (p.id === 'hardTop'
                                                    ? 'bottom-[calc(100%+16px)] md:bottom-[calc(100%+32px)] left-1/2 -translate-x-1/2'
                                                    : 'bottom-[calc(100%+32px)] md:bottom-[calc(100%+64px)] left-1/2 -translate-x-1/2')
                                                : 'top-[calc(100%+32px)] md:top-[calc(100%+64px)] left-1/2 -translate-x-1/2'
                                        }`}
                                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                                >
                                    <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className={`bg-black/60 backdrop-blur-xl border border-white/10 px-2 py-1 md:px-4 md:py-3 rounded-lg text-white shadow-2xl ${language === 'ar' ? 'font-arabic' : ''}`}>
                                        <p className={`text-[6px] sm:text-[10px] md:text-sm font-bold tracking-wide leading-relaxed ${(p.id === 'lamp' || p.id === 'mark') ? '' : 'text-nowrap whitespace-nowrap'}`}>{p.label}</p>
                                    </motion.div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 7. Safety Body */}
            <section className="bg-white">
                <div className="relative h-screen max-h-[1000px] w-full bg-zinc-900">
                    <div className="absolute inset-0 w-full h-full">
                        <ResponsiveImage
                            image="/assets/images/bj30-hybrid/safety-body.jpg"
                            imageAlt="Safety Body Structure"
                            className="w-full h-full"
                            imgClassName="object-cover"
                        />
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pb-12 pt-32">
                        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                            >
                                <div className="text-sm md:text-2xl font-bold mb-2">{t.safety.stats.steel.value}</div>
                                <div className="text-xs md:text-sm font-medium tracking-widest uppercase opacity-90">{t.safety.stats.steel.label}</div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                            >
                                <div className="text-sm md:text-2xl font-bold mb-2">{t.safety.stats.stiffness.value}</div>
                                <div className="text-xs md:text-sm font-medium tracking-widest uppercase opacity-90">{t.safety.stats.stiffness.label}</div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                viewport={{ once: true }}
                            >
                                <div className="text-sm md:text-2xl font-bold mb-2">{t.safety.stats.positions.value}</div>
                                <div className="text-xs md:text-sm font-medium tracking-widest uppercase opacity-90">{t.safety.stats.positions.label}</div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                <div className="max-w-5xl mx-auto px-6 py-16 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-6">
                            {t.safety.tag}
                        </h2>
                        <div className="h-px bg-gray-300 w-full mb-8"></div>
                        <p className="text-base text-gray-600 leading-relaxed max-w-4xl mx-auto">
                            {t.safety.desc}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* 8. Specifications */}
            <section className="bg-slate-950 py-24 text-white relative overflow-hidden">
                {/* Background Grid Pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <span className="text-red-600 font-bold tracking-widest uppercase">{t.specs.tag}</span>
                        <h2 className="text-4xl md:text-6xl font-black mt-4">{t.specs.title}</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Power, ...t.specs.items.engine },
                            { icon: Settings, ...t.specs.items.transmission },
                            { icon: Zap, ...t.specs.items.power },
                            { icon: Gauge, ...t.specs.items.torque },
                            { icon: Activity, ...t.specs.items.drive },
                            { icon: Ruler, ...t.specs.items.ground },
                            { icon: TrendingUp, ...t.specs.items.approach },
                            { icon: TrendingDown, ...t.specs.items.departure }
                        ].map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: idx * 0.05 }}
                                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors group"
                            >
                                <item.icon className="w-10 h-10 text-red-600 mb-6 group-hover:scale-110 transition-transform" />
                                <h3 className="text-gray-400 text-sm uppercase tracking-wider mb-2">{item.label}</h3>
                                <div className="text-2xl font-bold text-white mb-1">{item.value}</div>
                                {item.sub && <div className="text-gray-500 text-sm">{item.sub}</div>}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 8. Footer CTA */}
            <section className="py-24 bg-red-600 text-white text-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-4xl md:text-6xl font-bold mb-6">{t.footer.title}</h2>
                    <p className="text-xl md:text-2xl font-light mb-8 max-w-3xl mx-auto leading-relaxed">
                        {t.footer.desc}
                    </p>
                    <div className="flex flex-col md:flex-row justify-center gap-6">
                        <Link to="/test-drive">
                            <Button className="bg-white text-red-600 rounded-full px-12 py-8 text-lg font-bold hover:bg-gray-100 min-w-[200px] shadow-2xl">
                                {t.footer.btn}
                            </Button>
                        </Link>
                    </div>
                </motion.div>
            </section>

            <CatalogueModal
                isOpen={isCatalogueOpen}
                onClose={() => setIsCatalogueOpen(false)}
                modelName="BJ30"
            />
        </div>
    );
};

export default BJ30;
